import { ChangeDetectorRef, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs';
import { toast } from 'ngx-sonner';
import { CheckboxModule } from 'primeng/checkbox';
import { selectBranchId, selectUserUuid } from '../../../../core/store/auth/auth.selectors';
import { PatientRequiredFieldsService } from '../../../../core/services/patient-required-fields.service';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import {
  CONTACT_FIELD_KEYS,
  DEFAULT_REQUIRED_PATIENT_FIELDS,
  hasContactField,
  LOCKED_REQUIRED_FIELDS,
  PATIENT_FIELD_GROUPS,
  PatientFieldGroup,
  PATIENT_FIELD_LABEL_KEYS,
  PatientFieldKey,
  PatientRequiredFieldMap,
} from '../../../../core/models/patient-required-fields.model';

/** One PrimeIcons glyph per group, purely decorative - keyed by the modal step
 * the group mirrors so it can never drift out of sync with PATIENT_FIELD_GROUPS. */
const GROUP_ICONS: Record<1 | 2 | 3 | 4, string> = {
  1: 'pi-user',
  2: 'pi-id-card',
  3: 'pi-phone',
  4: 'pi-map-marker',
};

/**
 * Settings > Campos do Paciente — which fields staff must fill when creating or
 * editing a patient. Stored per branch in patient_required_fields_tb and applied
 * by src/app/pages/patients/patient-modal.
 *
 * Grouped by the modal's own four steps, using the same label keys, so a row
 * here and the box it governs always read the same.
 */
@Component({
  selector: 'app-patient-fields-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CheckboxModule,
    AppButton,
    HasPermissionDirective,
  ],
  templateUrl: './patient-fields-tab.html',
})
export class PatientFieldsTab implements OnInit {
  private store = inject(Store);
  private requiredFields = inject(PatientRequiredFieldsService);
  private translate = inject(TranslateService);
  private perm = inject(PermissionService);
  private cdr = inject(ChangeDetectorRef);

  get canEdit(): boolean {
    return this.perm.canEdit(MODULE.CONFIGURACOES);
  }

  readonly groups = PATIENT_FIELD_GROUPS;
  readonly labelKeys = PATIENT_FIELD_LABEL_KEYS;

  branchId: number | null = null;
  userUuid: string | null = null;
  loading = true;
  isSaving = signal(false);

  /** Local working copy — nothing reaches the database until Save. */
  values: PatientRequiredFieldMap = { ...DEFAULT_REQUIRED_PATIENT_FIELDS };

  isLocked(key: PatientFieldKey): boolean {
    return LOCKED_REQUIRED_FIELDS.includes(key);
  }

  /** Marks the contact rows so the template can flag the group when none is on. */
  isContactField(key: PatientFieldKey): boolean {
    return CONTACT_FIELD_KEYS.includes(key);
  }

  groupIcon(group: PatientFieldGroup): string {
    return GROUP_ICONS[group.step];
  }

  /** How many of this group's fields are currently mandatory, for the small
   * per-section count badge - purely a read of local state, so it updates live
   * as the clinic toggles boxes, before Save. */
  selectedCount(group: PatientFieldGroup): number {
    return group.keys.reduce((n, key) => n + (this.values[key] ? 1 : 0), 0);
  }

  /**
   * A policy with no mandatory contact field lets staff register a patient nobody
   * can reach — no reminder, form link or invoice can be delivered. Blocks Save
   * rather than warning after the fact.
   */
  get hasContactSelected(): boolean {
    return hasContactField(this.values);
  }

  ngOnInit() {
    this.store
      .select(selectUserUuid)
      .pipe(take(1))
      .subscribe((uuid) => (this.userUuid = uuid ?? null));

    this.store
      .select(selectBranchId)
      .pipe(
        filter((id) => !!id),
        take(1),
      )
      .subscribe((id) => {
        this.branchId = Number(id);
        // The service caches per branch, so this is usually already resolved and
        // the subscription below fires immediately.
        this.requiredFields.refresh(this.branchId);
      });

    this.requiredFields.requiredFields$.pipe(take(1)).subscribe((map) => {
      this.values = { ...map };
      this.loading = false;
      this.cdr.detectChanges();
    });
  }

  /** Local only — the clinic still has to press Save. */
  restoreDefaults() {
    if (!this.canEdit) return;
    this.values = { ...DEFAULT_REQUIRED_PATIENT_FIELDS };
  }

  save() {
    // Defence in depth behind the template's *hasPermission on the button.
    if (!this.canEdit || !this.branchId) return;

    // Also guarded by [disabled] on the button; re-checked here so the rule holds
    // however save() is reached.
    if (!this.hasContactSelected) {
      toast.error(this.translate.instant('patient_fields_contact_required_error'));
      return;
    }

    this.isSaving.set(true);
    this.requiredFields.save(this.branchId, this.values, this.userUuid).subscribe({
      next: () => {
        this.isSaving.set(false);
        toast.success(this.translate.instant('patient_fields_save_success'));
      },
      error: (err: any) => {
        this.isSaving.set(false);
        console.error('Error saving patient required fields:', err);
        toast.error(this.translate.instant('patient_fields_save_error'));
      },
    });
  }
}
